import { useState, useEffect, useRef, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useSelector, useDispatch } from "react-redux"
import { setCurrentTeam } from "../store/reducers/user"
import { useParams, useNavigate } from "react-router-dom"
import { videoService } from "../services/video"
import { Login } from "./login"
import { Loader } from "../cmps/loader"
import { ErrorPage } from "./error"
import { rolesRank } from "../services/data"
import { IdeaMaker } from "../cmps/video/idea-maker"
import { Researcher } from "../cmps/video/researcher"
import { Scripter } from "../cmps/video/scripter"
import { Producer } from "../cmps/video/producer"
import { calculateDaysDifference } from "../services/util"
import { httpService } from "../services/http"

export function Video() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { id } = useParams()
    const user = useSelector(state => state.user)
    const { currentTeam } = user
    const [video, setVideo] = useState('loading')
    const [memberHighestRole, setMemberHighestRole] = useState('')
    const [department, setDepartment] = useState(null)
    const [roles, setRoles] = useState([])
    const [isDelete, setIsDelete] = useState(false)
    const deleteReason = useRef(null)
    const [ideaMakerOpen, setIdeaMakerOpen] = useState(false)
    const [researcherOpen, setResearcherOpen] = useState(false)
    const [scripterOpen, setScripterOpen] = useState(false)
    const [producerOpen, setProducerOpen] = useState(false)

    useEffect(() => {
        setup()
        window.addEventListener('beforeunload', updateLastViewed)
        return () => {
            updateLastViewed()
            window.removeEventListener('beforeunload', updateLastViewed)
        }
    }, [])

    const setup = async () => {
        try {
            const { video: v, team: t } = await videoService.getVideoAndTeam(id)
            const member = t.members.find(member => member._id === user._id)
            if (!member) return setVideo('error')
            const h = member.roles.map(role => ({ role, rank: rolesRank[role] })).sort((a, b) => b.rank - a.rank)[0]
            dispatch(setCurrentTeam(t))
            setMemberHighestRole(h)
            setRoles(member.roles)
            setVideo(v)
            setDepartment(t.departments[v.status.departmentName])
            if (v.status.level === 'ideaMaker' || member.roles.includes('thumbnailEditor')) setIdeaMakerOpen(true)
            else if (v.status.level === 'researcher') setResearcherOpen(true)
            else if (v.status.level === 'scripter') setScripterOpen(true)
            else if (v.status.level === 'producer') setProducerOpen(true)
        }
        catch (error) {
            console.log(error)
            setVideo('error')
        }
    }

    const deleteVideo = async () => {
        try {
            await videoService.deleteVideo(video._id, currentTeam._id, deleteReason.current.value)
            queryClient.invalidateQueries({ queryKey: [currentTeam._id] })
            navigate('/')
        }
        catch (error) {
            console.log(error)
        }
    }

    const updateLastViewed = async () => {
        httpService.sendBeacon('handle-video/update-last-viewed', JSON.stringify({ videoId: id, teamId: currentTeam._id }))
    }

    const handleScripter = () => {
        if (!['producer', 'scripter', 'archived'].includes(video.status.level)) return null
        if (roles.includes('admin')) return 'admin'
        return 'scripter'
    }

    const handleProducer = () => {
        if (!['producer', 'archived'].includes(video.status.level)) return null
        if (roles.includes('admin')) return 'admin'
        return 'producer'
    }

    const getStatusClass = (l) => {
        if (video.status.level !== l || !isAdmin || !video.status.status.includes('forward')) return ''
        if (video.status.status === 'forward-as-failure') return 'red-text'
        if (video.status.status === 'forward-as-success') return 'green-text'
        return ''
    }

    const openAndScroll = (role) => {
        if (role === 'ideaMaker') setIdeaMakerOpen(true)
        if (role === 'researcher') setResearcherOpen(true)
        if (role === 'scripter') setScripterOpen(true)
        const el = document.getElementById(role)
        if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 200)
    }

    const { workerName, days } = useMemo(() => {
        if (video === 'loading' || !currentTeam) return { workerName: null, days: null }
        if (video.status?.departmentName) return { workerName: video.status.departmentName, days: calculateDaysDifference(video.status.recievedAt) }
        const worker = currentTeam?.members?.find(member => member._id === video?.status?.workerId) || null
        return { workerName: worker?.name, days: calculateDaysDifference(video.status.recievedAt) }
    }, [video])

    if (!user._id) return <Login />

    if (video === 'loading') return <Loader />

    if (video === 'error') return <ErrorPage />

    const isAdmin = memberHighestRole.role === 'admin'
    const displayResearcher = memberHighestRole.rank >= rolesRank.researcher && video.status.level !== 'ideaMaker'
    const displayScripter = handleScripter()
    const displayProducer = handleProducer()
    const lastViewed = video.lastViewed?.[user._id] ? new Date(video.lastViewed[user._id]) : new Date()

    return <div className="video-wrapper">
        {window.innerWidth > 1200 && <div className="video-side-bar">
            <div className="back" onClick={() => navigate('/')}>
                <span className="material-symbols-outlined">chevron_right</span>
                <p>למסך הבית</p>
            </div>
            {video?.ideaMaker?.thumbnails?.[0] && <img src={video.ideaMaker.thumbnails[0]} alt="thumbnail" className="thumbnail" />}
            {video?.ideaMaker?.titles?.[0] && <h3 className="title">{video.ideaMaker.titles[0]}</h3>}
            <div className="btn" onClick={() => openAndScroll('ideaMaker')}>
                <span className="material-symbols-outlined">lightbulb</span>
                <p>רעיון</p>
            </div>
            {displayResearcher && <div className="btn" onClick={() => openAndScroll('researcher')}>
                <span className="material-symbols-outlined">search</span>
                <p>תחקיר {video.status.level === 'researcher' ? ` - ${workerName} (${days} ימים)` : ''}</p>
            </div>}
            {displayScripter && <div className="btn" onClick={() => openAndScroll('scripter')}>
                <span className="material-symbols-outlined">calendar_month</span>
                <p>תסריט {(video.status.level === 'scripter' || video.status.level === 'scripter') ? ` - ${workerName} (${days} ימים)` : ''}</p>
            </div>}
            {displayProducer && <div className="btn" onClick={() => openAndScroll('producer')}>
                <span className="material-symbols-outlined">calendar_month</span>
                <p>הפקה {(video.status.level === 'producer') ? ` - ${workerName} (${days} ימים)` : ''}</p>
            </div>}
            <div className="delete-wrapper">
                {isAdmin ? isDelete ? <>
                    <textarea placeholder="הסבר" ref={deleteReason} />
                    <button className="delete" onClick={deleteVideo}>לחץ כאן למחיקה סופית</button>
                    <button className="delete main3" onClick={() => setIsDelete(false)}>בטל</button>
                </> : <p className="delete-line-outer" onClick={() => setIsDelete(true)}>מחק סרטון</p> : null}
            </div>
        </div>}

        <div className="video">
            <h1>פרטי הסרטון</h1>
            <p className={'add-idea noselect ' + getStatusClass('ideaMaker')} onClick={() => setIdeaMakerOpen(!ideaMakerOpen)} >רעיון<span className="material-symbols-outlined">{ideaMakerOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</span></p>
            <IdeaMaker lastViewed={lastViewed} isAdmin={isAdmin} isEditable={(roles.includes('ideaMaker') && video.status.level === 'ideaMaker') || isAdmin} isThumbnailEditor={roles.includes('thumbnailEditor')} createdBy={video.createdBy} videoId={video._id} part={video.ideaMaker} status={video.status} isOpen={ideaMakerOpen} setIsOpen={setIdeaMakerOpen} />
            {displayResearcher && <>
                <p className={'add-idea noselect ' + getStatusClass('researcher')} onClick={() => setResearcherOpen(!researcherOpen)} >תחקיר<span className="material-symbols-outlined">{researcherOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</span></p>
                <Researcher lastViewed={lastViewed} isAdmin={isAdmin} isEditable={(roles.includes('researcher') && video.status.level === 'researcher') || isAdmin} part={video.researcher} status={video.status} videoId={video._id} isOpen={researcherOpen} setIsOpen={setResearcherOpen} />
            </>}
            {displayScripter && <>
                <p className={'add-idea noselect ' + getStatusClass('scripter')} onClick={() => setScripterOpen(!scripterOpen)} >תסריט<span className="material-symbols-outlined">{scripterOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</span></p>
                <Scripter lastViewed={lastViewed} role={displayScripter} part={video.scripter} status={video.status} videoId={video._id} isOpen={scripterOpen} setIsOpen={setScripterOpen} />
            </>}
            {displayProducer && <>
                <p className={'add-idea noselect ' + getStatusClass('producer')} onClick={() => setProducerOpen(!producerOpen)} >הפקה<span className="material-symbols-outlined">{producerOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</span></p>
                <Producer lastViewed={lastViewed} department={department} role={displayProducer} part={video.producer} status={video.status} videoId={video._id} isOpen={producerOpen} setIsOpen={setProducerOpen} />
            </>}
        </div>
        {(window.innerWidth < 1200 && isAdmin) ? isDelete ? <div className="delete-wrapper">
            <textarea placeholder="הסבר" ref={deleteReason} />
            <button className="delete" onClick={deleteVideo}>לחץ כאן למחיקה סופית</button>
            <button className="delete main3" onClick={() => setIsDelete(false)}>בטל</button>
        </div> : <p className="red-text" onClick={() => setIsDelete(true)}>מחק סרטון</p> : null}
    </div >
}